import { useEffect, useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { ModalContentWrapper } from '@/components/ui';
import { useCreateFeatureRequest } from '@/hooks/use-feature-requests';
import { cn } from '@/lib/cn';
import { FEATURE_CATEGORY_LABELS } from '@/types';
import type { FeatureCategory, FeaturePriority } from '@/types';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function FeatureRequestModal({ open, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<FeatureCategory>('UI_UX');
  const [priority, setPriority] = useState<FeaturePriority>('MEDIUM');
  const { mutate: createFeature, isPending } = useCreateFeatureRequest();

  useEffect(() => {
    if (!open) {
      setTitle('');
      setDescription('');
      setCategory('UI_UX');
      setPriority('MEDIUM');
    }
  }, [open]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;

    createFeature(
      { title, description, category, priority },
      {
        onSuccess: () => {
          onClose();
          setTitle('');
          setDescription('');
          setCategory('UI_UX');
          setPriority('MEDIUM');
        },
      },
    );
  }

  if (!open) return null;

  return (
    <ModalContentWrapper open={open} onClose={onClose} title="Request a Feature">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Feature Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Brief title of the feature..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            disabled={isPending}
          />
        </div>

        {/* Description */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Describe the feature in detail..."
            rows={4}
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            disabled={isPending}
          />
        </div>

        {/* Category & Priority */}
        <div className="grid grid-cols-2 gap-4">
          {/* Category */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value as FeatureCategory)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              disabled={isPending}
            >
              {Object.entries(FEATURE_CATEGORY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
            <select
              value={priority}
              onChange={e => setPriority(e.target.value as FeaturePriority)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              disabled={isPending}
            >
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>
        </div>

        {/* Current Page URL Info */}
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-800">
          Page: <code className="font-mono">{window.location.href}</code>
        </div>
      </form>

      <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
        <Button
          variant="secondary"
          type="button"
          onClick={onClose}
          disabled={isPending}
          className="w-fit"
        >
          Cancel
        </Button>
        <Button
          variant="secondary"
          type="submit"
          disabled={!title.trim() || !description.trim() || isPending}
          className={cn(
            'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors',
            isPending
              ? 'cursor-not-allowed bg-indigo-400'
              : 'bg-gray-600 hover:bg-gray-700 disabled:opacity-50',
          )}
        >
          {isPending && <Spinner size="lg" className="h-4 w-4 animate-spin" />}
          Submit Request
        </Button>
      </div>
    </ModalContentWrapper>
  );
}
